import { assert } from '../../lib/assert-sidecar'
import type { IIJ, IXY } from '../../lib/distanceUtil'
import { l } from '../../lib/imports'
import { common } from '../../main/common-sidecar'
import { chessCamera } from './chessCamera-sidecar'
import { chessGameMode_game } from './chessGameMode_game-sidecar'
import type { IChessPiece } from './chessPieces'
import { chessPieces } from './chessPieces-sidecar'
import type { IChessTilemapPart } from './chessTilemapPart'
import { chessTilemapPart } from './chessTilemapPart-sidecar'

export interface IChessTilemap {
  parts: IChessTilemapPart[]
  width: number
  height: number
}

let _obj: IChessTilemap | null = null
export function get() {
  assert.notNil(_obj, 'tilemap not created')
  return _obj
}

export const spotSize = 128

export function create(): IChessTilemap {
  let obj: IChessTilemap = {
    parts: [],
    width: 0,
    height: 0,
  }
  _obj = obj
  setSize(8, 8)
  return obj
}

function setSize(width: number, height: number) {
  let obj = get()
  obj.width = width
  obj.height = height
  obj.parts = []

  // TODO: kill existing?
  let odd = true
  for (let j = 0; j < height; j++) {
    odd = !odd
    for (let i = 0; i < width; i++) {
      odd = !odd
      let gs = chessTilemapPart.create(
        i,
        j,
        i * spotSize + spotSize / 2,
        j * spotSize + spotSize / 2,
        odd,
      )
      obj.parts.push(gs)
    }
  }
}

export function inRange(i: number, j: number) {
  let obj = get()
  return i >= 0 && i < obj.width && j >= 0 && j < obj.height
}

export function getUnsafe(i: number, j: number) {
  let obj = get()
  assert.t(inRange(i, j), 'not in range', i, j)
  return obj.parts[i + j * obj.width]
}

export function getSafe(i: number, j: number) {
  let obj = get()
  if (!inRange(i, j)) {
    return null
  }
  return obj.parts[i + j * obj.width]
}
export function getSafeIJ(iij: IIJ) {
  return getSafe(iij.i, iij.j)
}
export function getSafeXY(ixy: IXY) {
  let i = convertXtoI(ixy.x)
  let j = convertYtoJ(ixy.y)
  return getSafe(i, j)
}

export function getSafeOffset(iij: IIJ, oi: number, oj: number) {
  return getSafe(iij.i + oi, iij.j + oj)
}

export function update() {}

export function getIJAtMouse() {
  let s = common.getPhaserScene()
  let mx = s.input.mousePointer.x
  let my = s.input.mousePointer.y
  let i = convertSXtoI(mx)
  let j = convertSYtoJ(my)
  return { i, j }
}

export function convertXtoI(_x: number) {
  let i = Math.floor(_x / spotSize)
  return i
}

export function convertYtoJ(_y: number) {
  let j = Math.floor(_y / spotSize)
  return j
}

export function convertXYtoIJ(xy: IXY) {
  return { i: convertXtoI(xy.x), j: convertYtoJ(xy.y) }
}
export function convertSYtoJ(_y: number) {
  let cam = chessCamera.get()
  let zoom = chessCamera.getZoom()
  let cy = _y / zoom + cam.cameraY / zoom
  let j = Math.floor(cy / spotSize)
  return j
}

export function convertSXtoI(_x: number) {
  let cam = chessCamera.get()
  let zoom = chessCamera.getZoom()
  let cx = _x / zoom + cam.cameraX / zoom
  let i = Math.floor(cx / spotSize)
  return i
}

export function convertIJtoXY(i: number, j: number) {
  let x = convertItoX(i)
  let y = convertJtoY(j)
  return { x, y }
}
export function convertItoX(i: number) {
  let x = i * spotSize + spotSize / 2
  return x
}
export function convertJtoY(j: number) {
  let y = j * spotSize + spotSize / 2
  return y
}

export function showCanMove(obj: IChessPiece | null) {
  for (let j = 0; j < 8; j++) {
    for (let i = 0; i < 8; i++) {
      let t = getUnsafe(i, j)
      if (!obj) {
        t.imageCanMove.visible = false
      } else {
        let cm = canMove(obj, t)
        t.imageCanMove.visible = cm
        t.imageCanMove.tint = cm ? 0xffffff : 0xff0000
      }
    }
  }
}

export function canMove(obj: IChessPiece, t: IChessTilemapPart) {
  if (
    chessGameMode_game.getIsGameEnded() ||
    !chessGameMode_game.getIsGameStarted()
  ) {
    return false
  }
  if (obj.isDead) {
    return false
  }
  if (obj.energy < chessPieces.getMoveTime(obj)) {
    return false
  }
  if (t.i === obj.i && t.j === obj.j) {
    return false // Cannot move to where we are
  }
  let cm = obj.data.canMove(obj, t)
  if (l.some(t.pieces, (c) => c.team === obj.team)) {
    cm = false
  }
  return cm
}
